import { createFileRoute } from '@tanstack/react-router'
import { LegalPageShell } from '@/components/LegalPageShell'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
})

function PrivacyPage() {
  return (
    <LegalPageShell title="Privacy Policy" updated="September 13, 2026">
      <section className="space-y-4">
        <p className="text-sm leading-relaxed text-white/60">
          PeerPass operates the first workplace funding-circle platform: a way for employees to
          fund one another at 0% interest, secured by and paid through payroll. This policy
          explains what personal data we handle, why we handle it, and the choices you have.
        </p>
        <p className="text-sm leading-relaxed text-white/60">
          We treat salary, banking and personal data as sensitive. We never sell personal data,
          and we limit what we collect to what is needed to run the service and keep it secure.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">1. Data we collect</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/60">
          <li>
            <strong className="text-white/80">Account &amp; identity</strong> &mdash; first and last
            name, email address, a password, and (for employers) company name and country.
          </li>
          <li>
            <strong className="text-white/80">Billing</strong> &mdash; subscription tier and billing
            cycle, and payment details handled by our payment processor.
          </li>
          <li>
            <strong className="text-white/80">Employee &amp; payroll data</strong> &mdash; employee
            records, funding-circle participation, and contribution/payout amounts reconciled
            against payroll so deductions and payouts flow through your existing payroll provider.
          </li>
          <li>
            <strong className="text-white/80">Tax &amp; withholding data</strong> &mdash; tax
            residency country, tax identifiers (shown only masked), foreign tax identifiers, W-9 /
            W-8BEN completion status, US-person status, and withholding rate.
          </li>
          <li>
            <strong className="text-white/80">Legal signatory data</strong> &mdash; the full legal
            name, job title and corporate email of the person who signs the Terms of Service and
            Data Processing Agreement on a company&rsquo;s behalf, together with their typed
            signature.
          </li>
          <li>
            <strong className="text-white/80">Sales inquiries</strong> &mdash; the name, work email,
            company name, employee count and message you submit through our demo form.
          </li>
          <li>
            <strong className="text-white/80">Usage &amp; diagnostics</strong> &mdash; aggregate
            employer dashboards, error and performance telemetry, and your detected country (used
            to show regional pricing).
          </li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">2. How we use your data</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/60">
          <li>To create and administer your account and provide the funding-circle service.</li>
          <li>
            To run contributions and payouts through payroll, and to cap circle amounts against
            salary for risk protection.
          </li>
          <li>To generate tax forms and the annual employer tax data package.</li>
          <li>To manage subscriptions, trials and payments.</li>
          <li>To respond to demo and sales requests.</li>
          <li>To secure, monitor and improve the platform.</li>
          <li>To meet legal and compliance obligations.</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">3. Legal bases</h2>
        <p className="text-sm leading-relaxed text-white/60">
          We process personal data where it is necessary to perform our contract with you, where
          you or your employer have given consent, where it is in our legitimate interest (such as
          keeping the platform secure), or where the law requires it. Tax and payroll data is
          processed on your employer&rsquo;s instructions as part of the service they provide you.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">4. How we share data</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-white/60">
          <li>
            <strong className="text-white/80">Your employer</strong> &mdash; employers administer
            their employees and receive the payroll and tax data needed to run deductions and tax
            filing.
          </li>
          <li>
            <strong className="text-white/80">Your payroll provider</strong> &mdash; to execute
            deductions and payouts through the payroll you already run.
          </li>
          <li>
            <strong className="text-white/80">Service providers</strong> &mdash; our payment
            processor, cloud hosting, and error-monitoring services, each bound to use data only to
            provide their service to us.
          </li>
          <li>
            <strong className="text-white/80">Where required by law</strong> &mdash; in response to
            a valid legal request or to protect rights, safety and property.
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-white/60">
          We do not sell personal data, and we do not share it with third parties for their own
          marketing.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">5. Anonymity in funding circles</h2>
        <p className="text-sm leading-relaxed text-white/60">
          PeerPass uses a blind-pool system. Employees see a circle&rsquo;s parameters (amount and
          duration) and join anonymously &mdash; circle members never see each other&rsquo;s names
          or identities. Privacy is a core part of the product, not an add-on.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">6. Security &amp; retention</h2>
        <p className="text-sm leading-relaxed text-white/60">
          We encrypt data at rest with AES-256 and in transit with TLS 1.3, and we are building
          towards SOC 2 Type II. We operate on a non-custodial model &mdash; we do not hold
          employee funds. We retain personal data only for as long as needed to provide the service
          and meet legal obligations.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">7. Your rights</h2>
        <p className="text-sm leading-relaxed text-white/60">
          Depending on your jurisdiction, you may have rights to access, correct, delete, or
          restrict the processing of your personal data, and to data portability. Where we process
          data on your employer&rsquo;s behalf, please contact your employer first; otherwise
          contact us and we will route your request appropriately.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">8. Children</h2>
        <p className="text-sm leading-relaxed text-white/60">
          PeerPass is a workplace service and is not directed at children under 16. We do not
          knowingly collect personal data from children.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-white">9. Changes &amp; contact</h2>
        <p className="text-sm leading-relaxed text-white/60">
          We may update this policy from time to time. We will post the revised version on this
          page with a new effective date. If you have any questions about this policy, please{' '}
          <a href="/contact" className="text-[#00E5A0] hover:underline">
            contact us
          </a>.
        </p>
      </section>
    </LegalPageShell>
  )
}